import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { SlidersHorizontal, Save } from "lucide-react";

const TIERS = ["retail", "shopkeeper", "dealer", "distributor"];
const TIER_STYLE = {
  retail: "text-slate-900", shopkeeper: "text-emerald-600",
  dealer: "text-blue-600", distributor: "text-lime-600",
};

export default function Slabs() {
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () =>
    api.get("/products").then((r) => {
      setProducts(r.data);
      setDrafts(Object.fromEntries(r.data.map((p) => [p.product_id, { ...p.prices, gst_rate: p.gst_rate, purchase_cost: p.purchase_cost || 0 }])));
    }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveRow = async (p) => {
    setSaving(p.product_id);
    try {
      const d = drafts[p.product_id];
      await api.put(`/products/${p.product_id}`, {
        name: p.name, sku: p.sku, hsn: p.hsn, unit: p.unit,
        gst_rate: Number(d.gst_rate), stock: p.stock, reorder_level: p.reorder_level,
        purchase_cost: Number(d.purchase_cost || 0),
        prices: Object.fromEntries(TIERS.map((t) => [t, Number(d[t])])),
        custom: p.custom || {},
      });
      toast.success(`Slab updated — ${p.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  const setDraft = (pid, key, val) =>
    setDrafts((d) => ({ ...d, [pid]: { ...d[pid], [key]: val } }));

  return (
    <div data-testid="portal-price-slabs-view">
      <PageHeader title="Price Slab Matrix" sub="Purchase cost + all four tier prices, editable inline — invoices pick these up instantly" />

      {products.length === 0 ? (
        <EmptyState icon={SlidersHorizontal} title="No products yet" sub="Add products first, then tune their slabs here" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white/80 overflow-x-auto p-1">
          <table className="w-full text-sm min-w-[1050px]" data-testid="slab-matrix-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3 text-right">GST %</th>
                <th className="px-4 py-3 text-right text-amber-600">Purchase Cost</th>
                {TIERS.map((t) => (
                  <th key={t} className={`px-4 py-3 text-right ${TIER_STYLE[t]}`}>{t === "retail" ? "Retail · B2C" : t}</th>
                ))}
                <th className="px-4 py-3 text-right">Save</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {products.map((p) => {
                const d = drafts[p.product_id] || {};
                const cost = Number(d.purchase_cost || 0);
                const margin = cost > 0 && Number(d.retail) > 0 ? Math.round(((Number(d.retail) - cost) / Number(d.retail)) * 100) : null;
                return (
                  <tr key={p.product_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`slab-row-${p.product_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{p.name}</p>
                      <p className="text-xs text-slate-500 font-mono2">HSN {p.hsn || "—"}{margin !== null && <span className="text-emerald-500"> · {margin}% margin on retail</span>}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input type="number" step="0.01" value={d.gst_rate ?? ""} onChange={(e) => setDraft(p.product_id, "gst_rate", e.target.value)}
                        data-testid={`slab-gst-${p.product_id}`}
                        className="w-20 bg-white/90 border border-slate-300 rounded-lg px-2 py-1.5 text-right font-mono2 text-slate-900 focus:border-emerald-500 outline-none" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input type="number" step="0.01" value={d.purchase_cost ?? ""} onChange={(e) => setDraft(p.product_id, "purchase_cost", e.target.value)}
                        data-testid={`slab-cost-${p.product_id}`}
                        className="w-28 bg-white/90 border border-amber-700/50 rounded-lg px-2 py-1.5 text-right font-mono2 text-amber-600 focus:border-amber-500 outline-none" />
                    </td>
                    {TIERS.map((t) => (
                      <td key={t} className="px-4 py-3 text-right">
                        <input type="number" step="0.01" value={d[t] ?? ""} onChange={(e) => setDraft(p.product_id, t, e.target.value)}
                          data-testid={`slab-price-${t}-${p.product_id}`}
                          className={`w-28 bg-white/90 border border-slate-300 rounded-lg px-2 py-1.5 text-right font-mono2 ${TIER_STYLE[t]} focus:border-emerald-500 outline-none`} />
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => saveRow(p)} disabled={saving === p.product_id} data-testid={`slab-save-${p.product_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-semibold px-3.5 py-1.5 transition-colors duration-200 disabled:opacity-50">
                        <Save className="w-3.5 h-3.5" /> {saving === p.product_id ? "Saving…" : "Save"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {TIERS.map((t) => (
          <div key={t} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`slab-summary-${t}`}>
            <p className={`text-xs font-mono2 uppercase tracking-wider ${TIER_STYLE[t]}`}>{t === "retail" ? "Retail (B2C)" : t}</p>
            <p className="text-xs text-slate-500 mt-2">Applied automatically when a {t === "retail" ? "B2C client" : `${t} client`} is selected on an invoice.</p>
          </div>
        ))}
      </div>
    </div>
  );
}
